import {
  Box,
  Flex,
  Text,
  useColorStyle,
  useColorMode,
  Grid,
  theme,
  Stack,
  Skeleton,
} from '@tonic-one/react';

import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgStore } from '@Models/useOrgModel';
import { OperationsApi } from '@Apis';
import { useQuery } from '@tanstack/react-query';
import { ECharts } from 'src/ECharts';
import { EChartsType } from 'echarts/core';
import { LineSeriesOption } from 'echarts';
import { FeedbackStatisticView, EuqTimeRangeEnum } from '../../type';
import MenuWithLabel from '@Components/Common/Menu';
import { getUtcTimestamp } from 'src/utils/time';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import useChartDateFormatter from '@Dashboard/RiskUser/useChartDateFormatter';
import { baseLineSeriesOption } from 'src/ECharts/utils';
import merge from 'lodash/merge';

const APPROVAL_STATUS_COLORS = {
  waitingForApproval: '#F36FA0',
  approved: '#40E884',
  rejected: '#FABA2A',
  bypassed: '#10B4D3',
  notNeedApproval: '#8A8A8A',
};

const REPORTED_TYPE_COLORS = {
  spam: '#578AEF',
  phishing: '#FF7633',
  notARisk: '#04C45A',
};

const ANALYSIS_RESULT_COLORS = {
  spam: '#10B4D3',
  phishing: '#F24C4F',
  flagged: '#04CAA1',
  normal: '#F36FA0',
};

const CHART_VIEW_STORAGE_KEY = 'euq_chart_view_cecp';

const ChartSkeleton = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  return (
    <Box py="4x" px="6x" bg={colorStyle.background.secondary}>
      <Flex direction="column" height="100%">
        <Skeleton height="24px" width="120px" mb="4x" animation="wave" />
        <Skeleton height="314px" width="100%" animation="wave" />
      </Flex>
    </Box>
  );
};

export default function EuqChart({
  timeRange,
}: {
  timeRange: EuqTimeRangeEnum;
}) {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['operations', 'common']);
  const { orgId } = useOrgStore();
  const { offset } = useUtcOffsetStore();
  const { format } = useChartDateFormatter();

  const [viewChart, setViewChart] = useState<FeedbackStatisticView>(() => {
    const savedView = localStorage.getItem(CHART_VIEW_STORAGE_KEY);
    return (
      (savedView as FeedbackStatisticView) || FeedbackStatisticView.PieChart
    );
  });

  useEffect(() => {
    localStorage.setItem(CHART_VIEW_STORAGE_KEY, viewChart);
  }, [viewChart]);

  const viewChartMenuItems = useMemo(() => {
    const viewMap = {
      [FeedbackStatisticView.PieChart]: t('operations.euq.view.pieChart'),
      [FeedbackStatisticView.LineChart]: t('operations.euq.view.LineChart'),
    };
    return [
      FeedbackStatisticView.PieChart,
      FeedbackStatisticView.LineChart,
    ].map((item) => {
      return {
        key: item,
        value: viewMap[item],
      };
    });
  }, [t]);

  // Query for reported type statistics (category dimension)
  const { data: categoryData, isLoading: categoryLoading } = useQuery({
    queryKey: ['euqFeedbackStatistic', 'category', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'category',
      }),
    select: (data) => data.data,
  });

  // Query for approval status statistics (status dimension)
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['euqFeedbackStatistic', 'status', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'status',
      }),
    select: (data) => data.data,
  });

  // Query for analysis result statistics (result_category dimension)
  const { data: resultData, isLoading: resultLoading } = useQuery({
    queryKey: ['euqFeedbackStatistic', 'result_category', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'result_category',
      }),
    select: (data) => data.data,
  });

  const {
    approvalStatusData,
    reportedTypeData,
    analysisResultData,
    lineChartData,
  } = useMemo(() => {
    // Helper function to calculate total from API data array
    const calculateTotal = (dataArray: any[]) => {
      if (!dataArray || !Array.isArray(dataArray)) return 0;
      return dataArray.reduce((sum, item) => sum + (item.count || 0), 0);
    };

    // If no data available, return empty charts
    if (!categoryData && !statusData && !resultData) {
      return {
        approvalStatusData: {
          data: [
            {
              name: t('operations.euq.statusTab.created'),
              value: 0,
              color: APPROVAL_STATUS_COLORS.waitingForApproval,
            },
            {
              name: t('operations.euq.statusTab.approved'),
              value: 0,
              color: APPROVAL_STATUS_COLORS.approved,
            },
            {
              name: t('operations.euq.statusTab.rejected'),
              value: 0,
              color: APPROVAL_STATUS_COLORS.rejected,
            },
            {
              name: t('operations.euq.statusTab.bypassed'),
              value: 0,
              color: APPROVAL_STATUS_COLORS.bypassed,
            },
            {
              name: t('operations.euq.chart.notNeedApproval'),
              value: 0,
              color: APPROVAL_STATUS_COLORS.notNeedApproval,
            },
          ],
          total: 0,
        },
        reportedTypeData: {
          data: [
            {
              name: t('operations.euq.reportAs.type.spam'),
              value: 0,
              color: REPORTED_TYPE_COLORS.spam,
            },
            {
              name: t('operations.euq.reportAs.type.phishing'),
              value: 0,
              color: REPORTED_TYPE_COLORS.phishing,
            },
            {
              name: t('operations.euq.reportAs.type.noRisk'),
              value: 0,
              color: REPORTED_TYPE_COLORS.notARisk,
            },
          ],
          total: 0,
        },
        analysisResultData: {
          data: [
            {
              name: t('operations.euq.reportAs.type.spam'),
              value: 0,
              color: ANALYSIS_RESULT_COLORS.spam,
            },
            {
              name: t('operations.euq.reportAs.type.phishing'),
              value: 0,
              color: ANALYSIS_RESULT_COLORS.phishing,
            },
            {
              name: t('operations.euq.result.flagged'),
              value: 0,
              color: ANALYSIS_RESULT_COLORS.flagged,
            },
            {
              name: t('operations.euq.reportAs.type.noRisk'),
              value: 0,
              color: ANALYSIS_RESULT_COLORS.normal,
            },
          ],
          total: 0,
        },
        lineChartData: undefined,
      };
    }

    // Process reported type data (category dimension)
    const spamTotal = calculateTotal(categoryData?.spam || []);
    const phishingTotal = calculateTotal(categoryData?.phishing || []);
    const notspamTotal = calculateTotal(categoryData?.notspam || []);

    // Process approval status data (status dimension)
    const createdTotal = calculateTotal(statusData?.created || []);
    const approvedTotal = calculateTotal(statusData?.approved || []);
    const rejectedTotal = calculateTotal(statusData?.rejected || []);
    const bypassedTotal = calculateTotal(statusData?.bypassed || []);

    // Process analysis result data (result_category dimension)
    const spamResultTotal = calculateTotal(resultData?.spam || []);
    const threatTotal = calculateTotal(resultData?.threat || []); // threat = phishing in analysis
    const flaggedTotal = calculateTotal(resultData?.flagged || []);
    const normalTotal = calculateTotal(resultData?.normal || []);

    const reportedTypeData = {
      data: [
        {
          name: t('operations.euq.reportAs.type.spam'),
          value: spamTotal,
          color: REPORTED_TYPE_COLORS.spam,
        },
        {
          name: t('operations.euq.reportAs.type.phishing'),
          value: phishingTotal,
          color: REPORTED_TYPE_COLORS.phishing,
        },
        {
          name: t('operations.euq.reportAs.type.noRisk'),
          value: notspamTotal,
          color: REPORTED_TYPE_COLORS.notARisk,
        },
      ],
      total: spamTotal + phishingTotal + notspamTotal,
    };

    const approvalStatusData = {
      data: [
        {
          name: t('operations.euq.statusTab.created'),
          value: createdTotal,
          color: APPROVAL_STATUS_COLORS.waitingForApproval,
        },
        {
          name: t('operations.euq.statusTab.approved'),
          value: approvedTotal,
          color: APPROVAL_STATUS_COLORS.approved,
        },
        {
          name: t('operations.euq.statusTab.rejected'),
          value: rejectedTotal,
          color: APPROVAL_STATUS_COLORS.rejected,
        },
        {
          name: t('operations.euq.statusTab.bypassed'),
          value: bypassedTotal,
          color: APPROVAL_STATUS_COLORS.bypassed,
        },
      ],
      total: createdTotal + approvedTotal + rejectedTotal + bypassedTotal,
    };

    const analysisResultData = {
      data: [
        {
          name: t('operations.euq.reportAs.type.spam'),
          value: spamResultTotal,
          color: ANALYSIS_RESULT_COLORS.spam,
        },
        {
          name: t('operations.euq.reportAs.type.phishing'),
          value: threatTotal,
          color: ANALYSIS_RESULT_COLORS.phishing,
        },
        {
          name: t('operations.euq.result.flagged'),
          value: flaggedTotal,
          color: ANALYSIS_RESULT_COLORS.flagged,
        },
        {
          name: t('operations.euq.reportAs.type.noRisk'),
          value: normalTotal,
          color: ANALYSIS_RESULT_COLORS.normal,
        },
      ],
      total: spamResultTotal + threatTotal + flaggedTotal + normalTotal,
    };

    // Create line chart data for all three chart types
    let lineChartData;
    if (categoryData || statusData || resultData) {
      lineChartData = {
        approvalStatus: {
          series: [
            {
              name: t('operations.euq.statusTab.created'),
              data: statusData?.created?.map((item: any) => item.count) || [],
              color: APPROVAL_STATUS_COLORS.waitingForApproval,
            },
            {
              name: t('operations.euq.statusTab.approved'),
              data: statusData?.approved?.map((item: any) => item.count) || [],
              color: APPROVAL_STATUS_COLORS.approved,
            },
            {
              name: t('operations.euq.statusTab.rejected'),
              data: statusData?.rejected?.map((item: any) => item.count) || [],
              color: APPROVAL_STATUS_COLORS.rejected,
            },
            {
              name: t('operations.euq.statusTab.bypassed'),
              data: statusData?.bypassed?.map((item: any) => item.count) || [],
              color: APPROVAL_STATUS_COLORS.bypassed,
            },
          ],
          xAxisData:
            statusData?.created?.map((item: any) =>
              format({
                timestamp: getUtcTimestamp(item.date, offset),
                interval: timeRange as any,
              })
            ) || [],
        },
        reportedType: {
          series: [
            {
              name: t('operations.euq.reportAs.type.spam'),
              data: categoryData?.spam?.map((item: any) => item.count) || [],
              color: REPORTED_TYPE_COLORS.spam,
            },
            {
              name: t('operations.euq.reportAs.type.phishing'),
              data:
                categoryData?.phishing?.map((item: any) => item.count) || [],
              color: REPORTED_TYPE_COLORS.phishing,
            },
            {
              name: t('operations.euq.reportAs.type.noRisk'),
              data: categoryData?.notspam?.map((item: any) => item.count) || [],
              color: REPORTED_TYPE_COLORS.notARisk,
            },
          ],
          xAxisData:
            categoryData?.spam?.map((item: any) =>
              format({
                timestamp: getUtcTimestamp(item.date, offset),
                interval: timeRange as any,
              })
            ) || [],
        },
        analysisResult: {
          series: [
            {
              name: t('operations.euq.reportAs.type.spam'),
              data: resultData?.spam?.map((item: any) => item.count) || [],
              color: ANALYSIS_RESULT_COLORS.spam,
            },
            {
              name: t('operations.euq.reportAs.type.phishing'),
              data: resultData?.threat?.map((item: any) => item.count) || [],
              color: ANALYSIS_RESULT_COLORS.phishing,
            },
            {
              name: t('operations.euq.result.flagged'),
              data: resultData?.flagged?.map((item: any) => item.count) || [],
              color: ANALYSIS_RESULT_COLORS.flagged,
            },
            {
              name: t('operations.euq.reportAs.type.noRisk'),
              data: resultData?.normal?.map((item: any) => item.count) || [],
              color: ANALYSIS_RESULT_COLORS.normal,
            },
          ],
          xAxisData:
            resultData?.spam?.map((item: any) =>
              format({
                timestamp: getUtcTimestamp(item.date, offset),
                interval: timeRange as any,
              })
            ) || [],
        },
      };
    }

    return {
      approvalStatusData,
      reportedTypeData,
      analysisResultData,
      lineChartData,
    };
  }, [categoryData, statusData, resultData, t, format, offset, timeRange]);

  const loading = categoryLoading || statusLoading || resultLoading;

  const LineChartComponent = ({
    title,
    data,
    xAxisData,
  }: {
    title: string;
    data: { name: string; data: number[]; color: string }[];
    xAxisData: string[];
  }) => {
    const lineSeries: LineSeriesOption[] = data.map((item) =>
      merge(
        {
          type: 'line',
          name: item.name,
          data: item.data,
          color: item.color,
          showSymbol: false,
        } satisfies LineSeriesOption,
        baseLineSeriesOption
      )
    );

    // Check if all data values are 0
    const hasData = data.some((item) => item.data.some((value) => value > 0));

    return (
      <Box py="4x" px="6x" bg={colorStyle.background.secondary}>
        <Flex direction="column" height="100%">
          <Text size="lg" color={colorStyle.color.primary} mb="4x">
            {title}
          </Text>
          <Flex flex="1">
            {!hasData ? (
              <Flex
                flex="1"
                alignItems="center"
                justifyContent="center"
                height={314}
              >
                <Text color={colorStyle.color.secondary}>
                  {t('common.noData')}
                </Text>
              </Flex>
            ) : (
              <ECharts
                series={lineSeries}
                xAxis={{
                  data: xAxisData,
                  axisLabel: {
                    fontSize: 12,
                    margin: 12,
                    width: 100,
                    rotate: 0,
                    overflow: 'break',
                    align: 'center',
                  },
                  axisTick: {
                    show: false,
                  },
                }}
                yAxis={{
                  minInterval: 1,
                }}
                legend={{
                  type: 'scroll',
                  top: 'bottom',
                  itemGap: 8,
                  textStyle: {
                    fontSize: 12,
                  },
                }}
                grid={{
                  containLabel: true,
                  right: 12,
                  bottom: 36,
                  left: 12,
                }}
                height={314}
                animation={true}
                tooltip={{
                  trigger: 'axis',
                }}
              />
            )}
          </Flex>
        </Flex>
      </Box>
    );
  };

  const PieChartWithLegend = ({
    title,
    data,
    totalValue,
    centerSubtext,
  }: {
    title: string;
    data: { name: string; value: number; color: string }[];
    totalValue: number;
    centerSubtext: string;
  }) => {
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const chartInstanceRef = useRef<EChartsType | null>(null);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    useEffect(() => {
      const checkScreenSize = () => {
        setIsSmallScreen(window.innerWidth < 1565);
      };

      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);
      return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const handleLegendClick = useCallback(
      (name: string) => {
        const newSelectedItems = new Set(selectedItems);
        if (selectedItems.has(name)) {
          newSelectedItems.delete(name);
        } else {
          newSelectedItems.add(name);
        }
        setSelectedItems(newSelectedItems);

        // Use ECharts API to toggle legend selection
        if (chartInstanceRef.current) {
          chartInstanceRef.current.dispatchAction({
            type: 'legendToggleSelect',
            name,
          });
        }
      },
      [selectedItems]
    );

    const handleLegendHover = useCallback((name: string, isHover: boolean) => {
      if (!chartInstanceRef.current) return;

      // Use ECharts API for highlighting
      if (isHover) {
        chartInstanceRef.current.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          name,
        });
      } else {
        chartInstanceRef.current.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          name,
        });
      }
    }, []);

    const chart = useMemo(() => {
      return (
        <ECharts
          chartRef={chartInstanceRef}
          series={{
            type: 'pie',
            radius: ['60%', '85%'],
            center: ['50%', '50%'],
            itemStyle: {
              borderWidth: 0,
            },
            label: { show: false },
            labelLine: { show: false },
            data: data.map((item) => ({
              name: item.name,
              value: item.value,
              itemStyle: {
                color: item.color,
                borderWidth: 1,
                borderColor: (theme.colors as any)[
                  colorStyle.background.secondary
                ],
              },
            })),
          }}
          width={isSmallScreen ? 280 : 314}
          height={isSmallScreen ? 280 : 314}
          animation={true}
          legend={{ show: false }}
          xAxis={{ show: false }}
        />
      );
    }, [data, isSmallScreen]);

    return (
      <Box py="4x" px="6x" bg={colorStyle.background.secondary}>
        <Flex direction="column" height="100%">
          <Text size="lg" color={colorStyle.color.primary} mb="4x">
            {title}
          </Text>
          <Flex
            alignItems="center"
            justifyContent="center"
            flex="1"
            direction={isSmallScreen ? 'column' : 'row'}
          >
            <Box position="relative" flex="0 0 auto">
              {chart}
              <Stack
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                textAlign="center"
                gap="4x"
              >
                <Text
                  size="4xl"
                  fontWeight="700"
                  color={colorStyle.color.primary}
                  lineHeight="1"
                >
                  {totalValue}
                </Text>
                <Text
                  size="sm"
                  color={colorStyle.color.secondary}
                  lineHeight="1"
                >
                  {centerSubtext}
                </Text>
              </Stack>
            </Box>
            <Flex
              mt={isSmallScreen ? '4x' : 0}
              ml={!isSmallScreen ? '4x' : 0}
              flexWrap="wrap"
              justifyContent={isSmallScreen ? 'center' : 'flex-start'}
              direction={isSmallScreen ? 'row' : 'column'}
              gap={isSmallScreen ? '2x' : 0}
            >
              {data.map((item) => {
                const isSelected = !selectedItems.has(item.name);
                const opacity = isSelected ? 1 : 0.4;

                return (
                  <Flex
                    key={item.name}
                    alignItems="center"
                    mb={isSmallScreen ? 0 : '2x'}
                    mr={isSmallScreen ? '2x' : 0}
                    cursor="pointer"
                    userSelect="none"
                    onClick={() => handleLegendClick(item.name)}
                    onMouseEnter={() => handleLegendHover(item.name, true)}
                    onMouseLeave={() => handleLegendHover(item.name, false)}
                    opacity={opacity}
                  >
                    <Box
                      width="8px"
                      height="8px"
                      bg={item.color}
                      mr="2x"
                      borderRadius="50%"
                      opacity={opacity}
                    />
                    <Text
                      size="sm"
                      color={colorStyle.color.primary}
                      opacity={opacity}
                      whiteSpace="nowrap"
                    >
                      {item.name}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </Flex>
      </Box>
    );
  };

  return (
    <Box mt="4x">
      <Flex border="0px solid">
        <Text fontWeight="500" fontSize="20px" lineHeight="28px" px="4x">
          {t('operations.euq.summary.title')}
        </Text>
        <Flex px="16px" gap="2x" ml="auto">
          <MenuWithLabel
            menuItems={viewChartMenuItems}
            minWidth={200}
            selectedKey={viewChart}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              const value = (e.target as HTMLElement).getAttribute('value');
              if (value) {
                setViewChart(value as FeedbackStatisticView);
              }
            }}
            label={t('operations.euq.label.view')}
          />
        </Flex>
      </Flex>
      <Box p="4x">
        <Grid templateColumns="repeat(3, 1fr)" gap="4x">
          {viewChart === FeedbackStatisticView.PieChart ? (
            <>
              {loading ? (
                <ChartSkeleton />
              ) : (
                <PieChartWithLegend
                  title={t('operations.euq.chart.approvalStatus')}
                  data={approvalStatusData.data}
                  totalValue={approvalStatusData.total}
                  centerSubtext={t('operations.euq.label.total')}
                />
              )}
              {loading ? (
                <ChartSkeleton />
              ) : (
                <PieChartWithLegend
                  title={t('operations.euq.chart.reportedType')}
                  data={reportedTypeData.data}
                  totalValue={reportedTypeData.total}
                  centerSubtext={t('operations.euq.label.total')}
                />
              )}
              {loading ? (
                <ChartSkeleton />
              ) : (
                <PieChartWithLegend
                  title={t('operations.euq.chart.analysisResult')}
                  data={analysisResultData.data}
                  totalValue={analysisResultData.total}
                  centerSubtext={t('operations.euq.chart.needAnalysis')}
                />
              )}
            </>
          ) : (
            <>
              {loading ? (
                <ChartSkeleton />
              ) : (
                <LineChartComponent
                  title={t('operations.euq.chart.approvalStatus')}
                  data={lineChartData?.approvalStatus?.series || []}
                  xAxisData={lineChartData?.approvalStatus?.xAxisData || []}
                />
              )}
              {loading ? (
                <ChartSkeleton />
              ) : (
                <LineChartComponent
                  title={t('operations.euq.chart.reportedType')}
                  data={lineChartData?.reportedType?.series || []}
                  xAxisData={lineChartData?.reportedType?.xAxisData || []}
                />
              )}
              {loading ? (
                <ChartSkeleton />
              ) : (
                <LineChartComponent
                  title={t('operations.euq.chart.analysisResult')}
                  data={lineChartData?.analysisResult?.series || []}
                  xAxisData={lineChartData?.analysisResult?.xAxisData || []}
                />
              )}
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
